/* eslint-disable max-params */
import { useRef } from 'react';

import { useToast } from '@chakra-ui/react';
import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import {
  type AccessRequest,
  type AnswerQuestionRequest,
  type CandidatePublicDto,
  CandidateService,
  type CvImprovementSuggestion,
  type InterviewQuestionResponse,
  InterviewService,
  MediaService,
  type PutCandidateFeedbackHistoryRequest,
  type QuestionPossibleAnswerResponse,
  type SupportAccountAnswerEdit,
  type UploadUrlPostResponse,
} from '@/services/api';

import { adminCandidateKeys } from '../admin-candidate/useAdminCandidateQueries';
import { useShareToken } from '../admin-interview';

type GetCandidateParams = {
  candidateId: string;
  token: string;
};

// Todo: add other query keys here
export const interviewQueryKeys = {
  getAllCandidatesQuestions: () => ['getAllCandidatesQuestions'] as const,
};

export const useGetCandidateQuery = (
  candidateParams: GetCandidateParams,
  options?: Partial<UseQueryOptions<CandidatePublicDto, AxiosError>>
) => {
  return useQuery<CandidatePublicDto, AxiosError>({
    queryKey: ['getAllCandidates'],
    queryFn: () =>
      CandidateService.getAllCandidates(
        {
          securityToken: '',
          ...candidateParams,
        },
        {
          overrideSecurityToken: candidateParams.token,
          ignoreRefresh: true,
        }
      ),
    ...options,
  });
};

export const useGetCandidateInterviewQuestion = (
  candidateParams: GetCandidateParams,
  options?: Partial<UseQueryOptions<InterviewQuestionResponse, AxiosError>>
) => {
  return useQuery<InterviewQuestionResponse, AxiosError>({
    queryKey: interviewQueryKeys.getAllCandidatesQuestions(),
    queryFn: () =>
      CandidateService.getAllCandidatesQuestions(
        {
          securityCode: '',
          ...candidateParams,
        },
        {
          overrideSecurityToken: candidateParams.token,
          ignoreRefresh: true,
        }
      ),
    ...options,
  });
};

type AnswerQuestionVariables = {
  questionId: string;
  candidateId: string;
  body: AnswerQuestionRequest;
};

export const useAnswerQuestionMutation = (
  token: string,
  options?: Partial<
    UseMutationOptions<
      InterviewQuestionResponse,
      AxiosError,
      AnswerQuestionVariables
    >
  >
) => {
  return useMutation<
    InterviewQuestionResponse,
    AxiosError,
    AnswerQuestionVariables
  >({
    mutationFn: ({ body, candidateId, questionId }) =>
      CandidateService.putCandidatesQuestionsAnswerById(
        {
          questionId,
          candidateId,
          body,
        },
        {
          overrideSecurityToken: token,
          ignoreRefresh: true,
        }
      ),
    ...options,
  });
};

type AnswerQuestionByAdminParams = {
  interviewsId: string;
  candidateId: string;
  questionId: string;
  body: AnswerQuestionRequest;
};

export const useAnswerQuestionByAdminMutation = (
  options?: Partial<
    UseMutationOptions<void, AxiosError, AnswerQuestionByAdminParams>
  >
) => {
  const shareToken = useShareToken();

  return useMutation<void, AxiosError, AnswerQuestionByAdminParams>({
    mutationFn: ({ body, candidateId, questionId, interviewsId }) =>
      InterviewService.putInterviewsCandidatesById1(
        {
          candidateId,
          interviewsId,
          questionId,
          body,
        },
        {
          overrideSecurityToken: shareToken,
          ...options?.axiosRequestConfig,
        }
      ),
    ...options,
  });
};

export const useMediaAccessMutation = (
  token: string,
  options?: Partial<
    UseMutationOptions<UploadUrlPostResponse, AxiosError, AccessRequest>
  >
) => {
  return useMutation<UploadUrlPostResponse, AxiosError, AccessRequest>({
    mutationFn: (body) =>
      MediaService.postMediaAccess(
        {
          body,
        },
        {
          overrideSecurityToken: token,
          ignoreRefresh: true,
        }
      ),
    ...options,
  });
};

type EarlyFinishQuestionParams = {
  candidateId: string;
  questionId: string;
};

export const useEarlyFinishQuestionMutation = (
  token: string,
  options?: Partial<
    UseMutationOptions<void, AxiosError, EarlyFinishQuestionParams>
  >
) =>
  useMutation<void, AxiosError, EarlyFinishQuestionParams>({
    mutationFn: ({ candidateId, questionId }) =>
      CandidateService.putCandidatesQuestionsEarlyFinishById(
        {
          candidateId,
          questionId,
        },
        {
          overrideSecurityToken: token,
          ignoreRefresh: true,
        }
      ),
    ...options,
  });

type AnswerQuestionBySupportAccountParams = {
  interviewsId: string;
  candidateId: string;
  questionId: string;
  body: SupportAccountAnswerEdit;
};

export const useAnswerQuestionBySupportAccountMutation = (
  candidateId: string,
  interviewsId: string,
  successMessage?: string,
  options?: Partial<
    UseMutationOptions<void, AxiosError, AnswerQuestionBySupportAccountParams>
  >
) => {
  const shareToken = useShareToken();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation(['interview']);

  return useMutation<void, AxiosError, AnswerQuestionBySupportAccountParams>({
    mutationFn: ({ body, candidateId, questionId, interviewsId }) =>
      InterviewService.putInterviewsCandidatesQuestionSupportEditById(
        {
          candidateId,
          interviewsId,
          questionId,
          body,
        },
        {
          overrideSecurityToken: shareToken,
          ...options?.axiosRequestConfig,
        }
      ),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: adminCandidateKeys.getInterviewCandidates({
          candidateId: candidateId,
          interviewsId: interviewsId,
        }),
        exact: false,
      });
      toast({
        status: 'success',
        title: successMessage ?? t('interview:questionUpdatedSuccessfully'),
      });
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export type PostInterviewQuestionPossibleAnswersRequest = {
  interviewsId: string;
  questionId: string;
  candidateId: string;
};

export const usePostInterviewQuestionAnswerPossibleByIdMutation = (
  options?: UseMutationOptions<
    Array<CvImprovementSuggestion>,
    AxiosError,
    PostInterviewQuestionPossibleAnswersRequest
  >
) => {
  const abortControllerRef = useRef<AbortController>();

  const mutation = useMutation<
    Array<CvImprovementSuggestion>,
    AxiosError,
    PostInterviewQuestionPossibleAnswersRequest
  >({
    mutationFn: async (body) => {
      const createResponse =
        await InterviewService.postInterviewsQuestionAnswerPossibleById(body);

      abortControllerRef.current = new AbortController();

      // Poll the results using the requestId until isWaiting is false
      let pollResponse: QuestionPossibleAnswerResponse;
      do {
        pollResponse =
          await InterviewService.getInterviewsQuestionAnswerPossibleStatusById(
            {
              interviewsId: body.interviewsId,
              questionId: body.questionId,
              requestId: createResponse,
              candidateId: body.candidateId,
            },
            {
              signal: abortControllerRef.current?.signal,
            }
          );

        if (pollResponse.isWaiting) {
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
      } while (
        pollResponse.isWaiting &&
        !abortControllerRef.current?.signal?.aborted
      );

      return pollResponse.answers ?? [];
    },

    ...options,
  });

  return {
    ...mutation,
    cancel: () => {
      abortControllerRef.current?.abort();
    },
  };
};

export const usePutAllCandidatesFeedbackMutation = (
  options?: UseMutationOptions<
    void,
    AxiosError,
    {
      candidateId: string;
      body: PutCandidateFeedbackHistoryRequest;
    }
  >
) => {
  return useMutation<
    void,
    AxiosError,
    {
      candidateId: string;
      body: PutCandidateFeedbackHistoryRequest;
    }
  >({
    mutationFn: async ({ candidateId, body }) => {
      return CandidateService.putAllCandidatesFeedback({
        candidateId,
        body,
      });
    },
    ...options,
  });
};

export const useApplyPenaltyTabChangeMutation = (
  token: string,
  options?: Partial<UseMutationOptions<void, AxiosError, void>>
) => {
  return useMutation<void, AxiosError, void>({
    mutationFn: () =>
      CandidateService.putCandidatesApplyPenaltyTabChange({
        overrideSecurityToken: token,
      }),
    ...options,
  });
};
